import { Button, Stack, Typography } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { sv } from "date-fns/locale";
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { getCurrentUser, getUsers } from "../api";
import { fetchSystemSettings } from "../api/system-settings";
import { useAuth } from "../context/AuthContext";
import { ThemeProvider } from "../context/ThemeContext";
import { syncSentryAuthState } from "../utils/sentry";
import { EmailSyncFailureBanner } from "./EmailSyncFailureBanner";
import {
	GlobalStateActions,
	GlobalStateProvider,
	useGlobalState,
	useGlobalStateDispatch,
} from "./globalState";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { NewVersionBanner } from "./NewVersionBanner";
import { Navbar } from "./navbar";
// removed: tags are now fetched inside person-search view
// removed: vote options are now fetched inside person-search view
import { PageLoader } from "./page-loader";
import BadgeAmountProvider from "./person/badge-amount-context";
import { PluginHealthBanner } from "./plugin-health-banner";
import { QueueHealthBanner } from "./QueueHealthBanner";
import { SendingSuspensionBanner } from "./sending-suspension-banner";
import { AppViewportProvider } from "./shared/app-viewport";
import SupportCaptureBubble from "./support/support-capture-bubble";
import { SupportReportProvider } from "./support/support-report-provider";

const AppContent = () => {
	const { isAuthenticated } = useAuth();
	const dispatch = useGlobalStateDispatch();
	const GlobalState = useGlobalState();
	const [retryStatus, setRetryStatus] = useState<{
		isRetrying: boolean;
		attempt: number;
		nextDelayMs: number | null;
		lastError?: string | null;
	}>({ isRetrying: false, attempt: 0, nextDelayMs: null, lastError: null });

	useEffect(() => {
		if (!isAuthenticated) return;

		let isCancelled = false;

		const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

		const retryWithBackoff = async <T,>(
			fn: () => Promise<T>,
			label: string,
			options?: {
				maxAttempts?: number;
				initialDelayMs?: number;
				maxDelayMs?: number;
				factor?: number;
				jitterMs?: number;
			}
		): Promise<T> => {
			const maxAttempts = options?.maxAttempts ?? 60;
			let delay = options?.initialDelayMs ?? 500;
			const maxDelay = options?.maxDelayMs ?? 8000;
			const factor = options?.factor ?? 2;
			const jitterMs = options?.jitterMs ?? 250;

			let attempt = 0;
			while (!isCancelled) {
				try {
					setRetryStatus((s) => ({ ...s, isRetrying: false }));
					return await fn();
				} catch (err) {
					attempt += 1;
					if (attempt >= maxAttempts) {
						throw err;
					}
					const jitter = Math.floor(Math.random() * jitterMs);
					const waitMs = Math.min(delay, maxDelay) + jitter;
					if (attempt === 1) {
						console.warn(`[startup] ${label} failed, retrying...`);
					}
					setRetryStatus({
						isRetrying: true,
						attempt,
						nextDelayMs: waitMs,
						lastError: (err as any)?.message
							? String((err as any).message)
							: String(err),
					});
					await sleep(waitMs);
					delay = Math.min(delay * factor, maxDelay);
				}
			}
			throw new Error("cancelled");
		};

		const run = async () => {
			try {
				const [users, currentUser, systemSettings] = await Promise.all([
					retryWithBackoff(() => getUsers(), "getUsers"),
					retryWithBackoff(() => getCurrentUser(), "getCurrentUser"),
					retryWithBackoff(
						() => fetchSystemSettings(),
						"fetchSystemSettings"
					),
				]);

				if (isCancelled) return;

				const colors: Record<number, string> = {} as Record<number, string>;
				users.forEach((candidate) => {
					if (candidate.userprofile) {
						colors[candidate.id] =
							candidate.userprofile.config["profile"]["badge_color"];
					}
				});

				dispatch({
					type: GlobalStateActions.UPDATE_USER_COLORS,
					data: { userColors: colors },
				});
				dispatch({
					type: GlobalStateActions.UPDATE_USERS,
					data: { users },
				});

				dispatch({
					type: GlobalStateActions.UPDATE_USER,
					data: { currentUser },
				});
				dispatch({
					type: GlobalStateActions.UPDATE_SYSTEM_SETTINGS,
					data: { systemSettings },
				});

				// Git version is exposed to staff; DB host/name only to superusers.
				if (currentUser.is_staff || currentUser.is_superuser) {
					try {
						const { fetchSystemInfo } = await import("../api/system-info");
						const sysInfo = await fetchSystemInfo();
						dispatch({
							type: GlobalStateActions.UPDATE_DATABASE_HOST,
							data: {
								databaseHost: sysInfo.database?.host ?? "",
								databaseName: sysInfo.database?.name ?? "",
								gitBranch: sysInfo.git_branch,
								gitCommit: sysInfo.git_commit,
								gitCommitVersion: sysInfo.git_commit_version,
								gitCommitDate: sysInfo.git_commit_date ?? "",
								stagingEnv: sysInfo.staging_env ?? "",
								stagingHostnames: sysInfo.staging_hostnames ?? [],
								migrations: sysInfo.migrations,
							},
						});
					} catch (error) {
						console.error("Failed to fetch system info:", error);
					}
				}

				syncSentryAuthState(currentUser, users);
			} catch (error) {
				console.error("Initial backend fetch failed after retries:", error);
				return;
			} finally {
				if (!isCancelled) {
					dispatch({
						type: GlobalStateActions.SET_LOADING,
						data: { loading: false },
					});
				}
			}
		};

		run();

		return () => {
			isCancelled = true;
		};
	}, [isAuthenticated, dispatch]);

	const location = useLocation();
	// No token / not authenticated: redirect to login instead of rendering the
	// app shell, whose bootstrap is gated behind isAuthenticated and would
	// otherwise leave `loading` true forever (infinite spinner). Preserve the
	// intended destination so login can return the user there.
	if (!isAuthenticated) {
		const next = encodeURIComponent(location.pathname + location.search);
		return <Navigate to={`/login?next=${next}`} replace />;
	}

	if (GlobalState?.loading) {
		return (
			<>
				<CssBaseline />
				<PageLoader />
				<Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
					<Typography variant="body2" color="text.secondary">
						{retryStatus.isRetrying
							? `Connecting to backend… retry ${retryStatus.attempt}` +
								(retryStatus.nextDelayMs
									? `, next attempt in ${Math.ceil(retryStatus.nextDelayMs / 1000)}s`
									: "")
							: "Initializing…"}
					</Typography>
					{retryStatus.lastError ? (
						<Typography
							variant="caption"
							color="text.disabled"
							sx={{ textAlign: "center", px: 2 }}
						>
							Last error: {retryStatus.lastError}
						</Typography>
					) : null}
					<Button
						size="small"
						variant="outlined"
						onClick={() => window.location.reload()}
					>
						Reload now
					</Button>
				</Stack>
			</>
		);
	}

	return (
		<ThemeProvider>
			<LocalizationProvider adapterLocale={sv} dateAdapter={AdapterDateFns}>
				<SupportReportProvider>
					<AppViewportProvider
						chrome={
							<>
								<Navbar />
								{/* Banners belong inside the app, under its own
							    header — above it they read as browser chrome.
							    Measured with the navbar, so every view that
							    fills the viewport shrinks when one appears. */}
								<NewVersionBanner />
								<ImpersonationBanner />
								<QueueHealthBanner />
								<EmailSyncFailureBanner />
								<PluginHealthBanner />
								<SendingSuspensionBanner />
							</>
						}
					>
						<BadgeAmountProvider>
							<Outlet />
						</BadgeAmountProvider>
					</AppViewportProvider>
					<SupportCaptureBubble />
				</SupportReportProvider>
			</LocalizationProvider>
		</ThemeProvider>
	);
};

export const App = () => {
	return (
		<GlobalStateProvider>
			<AppContent />
		</GlobalStateProvider>
	);
};
